import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { InertImage } from '@shared/components/markdown/InertImage';
import { cn } from '@shared/components/ui/cn';

interface MarkdownProps {
  children: string;
  className?: string;
}

/**
 * Renders the assistant's markdown (the orchestrator streams GitHub-flavored
 * markdown — bold, lists, links, tables) with our design tokens. Mirrors the
 * old CRM's react-markdown + remark-gfm approach; styled to match
 * RichTextDisplay so chat prose looks consistent with the rest of the app.
 *
 * Links open in a new tab; tables scroll horizontally on overflow. Images
 * render as their alt text and never load (see InertImage).
 */
export function Markdown({ children, className }: MarkdownProps) {
  return (
    <div
      className={cn(
        'max-w-none text-sm leading-relaxed text-foreground',
        '[&_h1]:mt-5 [&_h1]:mb-2 [&_h1]:text-xl [&_h1]:font-bold',
        '[&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-lg [&_h2]:font-semibold',
        '[&_h3]:mt-3 [&_h3]:mb-1.5 [&_h3]:text-base [&_h3]:font-semibold',
        '[&_h4]:mt-3 [&_h4]:mb-1 [&_h4]:font-medium',
        '[&_p]:mb-2.5 [&_p:last-child]:mb-0',
        '[&_strong]:font-semibold',
        '[&_ul]:mb-2.5 [&_ul]:list-disc [&_ul]:ps-5',
        '[&_ol]:mb-2.5 [&_ol]:list-decimal [&_ol]:ps-5',
        '[&_li]:mb-1',
        '[&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-primary/80',
        '[&_blockquote]:my-2.5 [&_blockquote]:border-s-2 [&_blockquote]:border-border [&_blockquote]:ps-3 [&_blockquote]:text-muted-foreground',
        '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-xs',
        '[&_pre]:my-2.5 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-3',
        '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
        // GFM tables
        '[&_table]:my-3 [&_table]:block [&_table]:w-full [&_table]:overflow-x-auto [&_table]:border-collapse',
        '[&_th]:border [&_th]:border-border [&_th]:bg-muted/50 [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-start [&_th]:text-xs [&_th]:font-medium',
        '[&_td]:border [&_td]:border-border [&_td]:px-3 [&_td]:py-1.5 [&_td]:text-sm',
        '[&_hr]:my-3 [&_hr]:border-border',
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ node: _node, children, ...props }) => (
            <a {...props} target="_blank" rel="noreferrer">
              {children}
            </a>
          ),
          img: InertImage,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
